import { useCallback, useState } from 'react';
import { uploadErrorMessage } from './validate.js';

export type UploadState = { state: 'waiting' } | { state: 'uploading' } | { state: 'done'; id: string } | { state: 'error'; message: string };

/**
 * Envoi des photos choisies, une par une (réseau mobile), avec l'état de chacune : en attente,
 * en cours, envoyée, erreur (message de l'API pour un format refusé). Une photo déjà envoyée
 * n'est pas renvoyée si l'on relance après une erreur.
 */
export function useUploads(upload: (file: File) => Promise<string>) {
  const [states, setStates] = useState<Map<File, UploadState>>(new Map());

  const run = useCallback(
    async (files: File[]): Promise<{ ids: string[]; failed: File[] }> => {
      const current = new Map(states);
      const set = (f: File, s: UploadState) => {
        current.set(f, s);
        setStates(new Map(current));
      };
      for (const f of files) if (current.get(f)?.state !== 'done') set(f, { state: 'waiting' });
      const ids: string[] = [];
      const failed: File[] = [];
      for (const f of files) {
        const before = current.get(f);
        if (before?.state === 'done') {
          ids.push(before.id);
          continue;
        }
        set(f, { state: 'uploading' });
        try {
          const id = await upload(f);
          set(f, { state: 'done', id });
          ids.push(id);
        } catch (err) {
          set(f, { state: 'error', message: uploadErrorMessage(err) });
          failed.push(f);
        }
      }
      return { ids, failed };
    },
    [states, upload],
  );

  const reset = useCallback(() => setStates(new Map()), []);
  const busy = [...states.values()].some((s) => s.state === 'uploading' || s.state === 'waiting');
  return { states, run, reset, busy };
}
